"use client";

import { ArrowPathIcon } from "@heroicons/react/24/outline";
import * as React from "react";
import Markdown from "react-markdown";
import { IChatMessage, LogEvent, MessageListProps } from "@/types/chat-types";
import { cn } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";

export default function MessageList({
  messages,
  sessionLogs = {},
  onRetry,
  onSubmit,
  loading = false,
  userInput,
  setUserInput,
}: MessageListProps) {
  const messageEndRef = React.useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();
  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const renderMessageContent = (text?: string) => {
    if (!text) return null;
    
    return (
      <div className="prose dark:prose-invert prose-sm max-w-none">
        <Markdown>{text}</Markdown>
      </div>
    );
  };

  const renderMessage = (message: IChatMessage, index: number) => {
    const isUser = message.sender === 'user';
    const isAssistant = message.sender === 'assistant';
    const showAvatar = !isAssistant && (index === 0 || messages[index - 1]?.sender !== message.sender);
    const showTimestamp = index === messages.length - 1 || 
      (messages[index + 1]?.sender !== message.sender) ||
      (new Date(message.timestamp || 0).getTime() - new Date(messages[index + 1]?.timestamp || 0).getTime() < -60000);

    return (
      <div 
        key={`${message.timestamp}-${index}`}
        className={cn(
          'flex w-full',
          isUser ? 'justify-end' : 'justify-start',
          isAssistant && 'justify-center',
          'group',
          'px-4 py-2',
          isAssistant ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
        )}
      >
        <div className={cn(
          'flex max-w-3xl w-full',
          isUser ? 'flex-row-reverse' : 'flex-row'
        )}>
          {/* Avatar */}
          <div className={cn(
            'flex-shrink-0 mt-1',
            isUser ? 'ml-3' : 'mr-3',
            !showAvatar && 'invisible'
          )}>
            <div className={cn(
              'h-8 w-8 rounded-full flex items-center justify-center',
              isUser 
                ? 'bg-blue-500 text-white' 
                : isAssistant 
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
            )}>
              {isUser ? (
                <span className="text-sm font-medium"> {user?.firstName?.[0]?.toUpperCase() || user?.lastName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}</span>
              ) : isAssistant ? (
                <span className="text-sm font-medium">!</span>
              ) : (
                <span className="text-sm font-medium">AI</span>
              )}
            </div>
          </div>

          {/* Message Bubble */}
          <div className="flex">
            <div 
              className={cn(
                'inline-block px-4 py-2 rounded-lg',
                isUser 
                  ? 'bg-blue-500 text-white rounded-br-none' 
                  : isAssistant
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none',
                'relative group/message',
                'transition-colors duration-200',
                'shadow-sm'
              )}
            >
              {renderMessageContent(message.text)}
              
              {showTimestamp && message.timestamp && (
                <div 
                  className={cn(
                    'text-xs mt-1 opacity-70',
                    isUser ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400',
                    'text-right',
                    'transition-opacity duration-200',
                    'group-hover/message:opacity-100',
                    'flex items-center justify-end space-x-1',
                    'pt-1'
                  )}
                >
                  <span>{formatTimestamp(message.timestamp)}</span>
                  {isUser && loading && index === messages.length - 1 && (
                    <span className="ml-1 inline-block h-2 w-2 rounded-full bg-blue-300 animate-pulse"></span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const onUploadCompleteHandler = async (fileName: string, error: any) => {
    if(error){
      console.log("File upload failed:", error)
      onSubmit("File upload failed..Please try again!")
    }else{
      console.log(`${fileName} file uploaded successfully`)
      onSubmit(`${fileName} file uploaded successfully`)
    }
    setUserInput(null);
  }

  const renderLogs = (sessionId: string) => {
    const logs = sessionLogs?.[sessionId] || [];
    if (logs.length === 0) return null;
    
    return (
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Debug Logs</div>
        <div className="space-y-2">
          {logs.map((log: LogEvent, index: number) => (
            <div
              key={`log-${sessionId}-${index}`}
              className="text-xs p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex justify-between items-center text-gray-500 dark:text-gray-400 mb-1">
                <span className="font-mono">{log.type}</span>
                <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
              </div>
              <div className="font-mono text-xs break-words">
                {log.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInput = () => {
    if (!userInput) return null;
    console.log("Rendering Input", userInput);
    return (
      <div 
      className="flex w-full justify-center group px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20"
    >
      <div className={cn(
        'flex max-w-3xl w-full',
        'flex-row'
      )}>
        {/* Avatar */}
        <div className={cn(
          'flex-shrink-0 mt-1',
          'mr-3',
          'invisible'
        )}>
          <div className={cn(
            'h-8 w-8 rounded-full flex items-center justify-center',
            'bg-blue-500 text-white'
          )}>
            <span className="text-sm font-medium">U</span>
          </div>
        </div>

        {/* Message Bubble */}
        <div className="flex-1">
          <div 
            className={cn(
              'inline-block px-4 py-2 rounded-lg',
              'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none',
              'relative group/message',
              'transition-colors duration-200',
              'shadow-sm'
            )}
          >
            {userInput?.data?.type =="FILE_UPLOAD" ? <FileUpload inputInfo={userInput} onUploadComplete={onUploadCompleteHandler}/> : ""}
          </div>
        </div>
      </div>
    </div>
    );
  };
 
  interface FileUploadProps {
    inputInfo: any;
    onUploadComplete: (fileName: string, error: any) => void;
  }

  const FileUpload: React.FC<FileUploadProps> = ({ inputInfo, onUploadComplete }) => {
    const [isUploading, setIsUploading] = React.useState(false);
    const [hasUploaded, setHasUploaded] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const data = inputInfo["data"];
  //  data =  {
  //     "type": "FILE_UPLOAD",
  //     "url": "https://agenticgenie-autogen-test1.s3.amazonaws.com/uploads/SendEmailIfSumTwenty_v2_1/20250615_072244_dd580c10a6574331b44fea85209905b9.bin?AWSAccessKeyId=AKIASLRTEQQJ654EMPWR&Signature=lW32I73sKHh0jjrfJsdmcjVrdrY%3D&content-type=application%2Foctet-stream&Expires=1749975764",
  //     "s3_key": "uploads/SendEmailIfSumTwenty_v2_1/20250615_072244_dd580c10a6574331b44fea85209905b9.bin",
  //     "bucket": "agenticgenie-autogen-test1",
  //     "text": "Kindly upload or type your Aadhaar Card Number. Ensure no special characters are included—only letters and numbers.",
  //     "input_id": "SendEmailIfSumTwenty_v2_1"
  // }
    const question = data.text || '';

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      console.log("File selected:", file, inputInfo);
      if (!file || !inputInfo) return;

      setIsUploading(true);
      try {
        const uploadResponse = await fetch(data.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': 'application/pdf',
          },
        });

        if (uploadResponse.ok) {
          setHasUploaded(true);
          console.log("File uploaded successfully:", file.name);
          onUploadComplete(file.name, null);
        } else {
          console.error('File upload failed');
          onUploadComplete(file.name, null);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        onUploadComplete(file.name, error);
      } finally {
        setIsUploading(false);
      }
    };

    const handleUploadClick = () => {
      fileInputRef.current?.click();
    };

    return (
      <div className="mt-2 m-2">
        {/* <p className="text-gray-600 dark:text-gray-300 mb-4">
          {inputInfo.data.text}
        </p> */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          disabled={hasUploaded}
        />
        <button
          onClick={handleUploadClick}
          disabled={isUploading || hasUploaded}
          className={cn(
            "px-4 py-2 rounded text-sm font-medium",
            "transition-colors duration-200",
            hasUploaded
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 cursor-not-allowed"
              : isUploading
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 cursor-wait"
              : "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
          )}
        >
          {isUploading
            ? "Uploading..."
            : hasUploaded
            ? "File Uploaded"
            : "Choose File"}
        </button>
        {hasUploaded && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            File uploaded successfully!
          </p>
        )}
      </div>
    );
  };

  // const TextInputPrompt: React.FC<{ question: string }> = ({ question }) => (
  //   <div className="prose dark:prose-invert prose-sm max-w-none">
  //     <p className="text-gray-600 dark:text-gray-300">
  //       {question}
  //     </p>
  //   </div>
  // );

  // const renderInput = (message: IChatMessage, index: number) => {
  //   if (!setUserInput || !message.arguments) return null;
  //   console.log("renderInput message", message);
  //   let inputInfo: InputInfo;
  //   try {
  //     inputInfo = JSON.parse(message.arguments) as InputInfo;
  //   } catch (e) {
  //     console.error('Failed to parse input arguments:', e);
  //     return null;
  //   }

  //   const isFileUpload = inputInfo.input_type === 'FILE_UPLOAD';
  //   const inputId = inputInfo.input_id;
  //   const question = inputInfo.question || '';
    
  //   if (isFileUpload && !inputInfo.url) {
  //     console.error('File upload requested but no URL provided');
  //     return null;
  //   }

  //   const handleUploadComplete = (fileName: string) => {
  //     setUserInput((prev: Record<string, string>) => ({
  //       ...prev,
  //       [inputId]: `File uploaded: ${fileName}`
  //     }));
  //   };

  //   return (
  //     <div 
  //       key={message.id}
  //       className="flex w-full justify-start group px-4 py-2"
  //     >
  //       <div className="flex max-w-3xl w-full">
  //         <div className="flex-1">
  //           <div className="inline-block px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none shadow-sm">
  //             <div className="prose dark:prose-invert prose-sm max-w-none">
  //               <p className="mb-2">{question}</p>
  //               {isFileUpload ? (
  //                 <FileUpload 
  //                   inputInfo={inputInfo} 
  //                   onUploadComplete={handleUploadComplete} 
  //                 />
  //               ) : (
  //                 <TextInputPrompt question={question} />
  //               )}
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="flex-1 overflow-y-auto p-2">
      <div className="space-y-1">
        {messages.map((message, index) => (
          renderMessage(message, index)
        ))}
        {renderInput()}
        {loading && (
          <div className="flex px-4 py-2">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce"></div>
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        )}
        <div ref={messageEndRef} />
      </div>
    </div>
  );
}